import React, { useState, useEffect } from "react";
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AutoStoriesOutlinedIcon from '@mui/icons-material/AutoStoriesOutlined';
import { Button } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import CircularProgress from "@mui/material/CircularProgress";
import { Typography } from "@mui/material";
import { RegisterStudent, GetColleges, GetDepartmentsByCollege } from "../../utils/axios/registerandloginAPI";
import {
  validateEmail,
  validatePhoneNumber,
  cleanPhoneNumber,
  validateName,
} from "../../utils/validate";
import LoadingPopup from "../Common/LoadingPopup";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";

const Register = () => {
  const navigate = useNavigate();

  // Form states
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [collegeId, setCollegeId] = useState("");
  const [departmentId, setDepartmentId] = useState("");

  // Dropdown data
  const [colleges, setColleges] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingColleges, setLoadingColleges] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  // Error states
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [collegeError, setCollegeError] = useState("");
  const [departmentError, setDepartmentError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [open, setOpen] = useState(false);

  // Dialog
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Fetch colleges on component mount
  useEffect(() => {
    const fetchColleges = async () => {
      setLoadingColleges(true);
      const response = await GetColleges();
      if (response.success) {
        setColleges(response.data || []);
      } else {
        setFormError("Failed to load colleges. Please refresh the page.");
      }
      setLoadingColleges(false);
    };
    fetchColleges();
  }, []);

  // Fetch departments when college changes
  useEffect(() => {
    if (!collegeId) {
      setDepartments([]);
      setDepartmentId("");
      return;
    }

    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      setDepartmentId(""); // Reset department when college changes
      const response = await GetDepartmentsByCollege(collegeId);
      if (response.success) {
        setDepartments(response.data || []);
      } else {
        setDepartments([]);
        setFormError("Failed to load departments. Please try again.");
      }
      setLoadingDepartments(false);
    };
    fetchDepartments();
  }, [collegeId]);

  // Reusable validator
  const updateError = (value, validateFunc, setErrorFunc, errorMessage) => {
    if (!value.trim()) {
      setErrorFunc("");
      return;
    }
    if (!validateFunc(value)) {
      setErrorFunc(errorMessage);
    } else {
      setErrorFunc("");
    }
  };

  // Live validation for text fields
  useEffect(() => {
    updateError(
      firstName,
      validateName,
      setFirstNameError,
      "Please enter a valid first name."
    );
    updateError(
      lastName,
      validateName,
      setLastNameError,
      "Please enter a valid last name."
    );
    updateError(
      email,
      validateEmail,
      setEmailError,
      "Please enter a valid email address."
    );
    updateError(
      phoneNumber,
      validatePhoneNumber,
      setPhoneError,
      "Please enter a valid 10-digit phone number."
    );
  }, [firstName, lastName, email, phoneNumber]);

  // Validate dropdown selections
  useEffect(() => {
    if (collegeId === "" && colleges.length > 0) {
      setCollegeError("");
    } else {
      setCollegeError("");
    }
  }, [collegeId, colleges.length]);

  useEffect(() => {
    if (departmentId === "" && departments.length > 0 && collegeId) {
      setDepartmentError("");
    } else {
      setDepartmentError("");
    }
  }, [departmentId, departments.length, collegeId]);

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    // Validate text fields
    if (firstNameError || lastNameError || emailError || phoneError) {
      setFormError("Please fix the highlighted errors before submitting.");
      return;
    }

    // Check required fields
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !phoneNumber.trim()) {
      setFormError("First name, last name, email, and phone number are required.");
      return;
    }

    if (!collegeId) {
      setCollegeError("Please select a college.");
      setFormError("Please select a college.");
      return;
    }

    if (!departmentId) {
      setDepartmentError("Please select a department.");
      setFormError("Please select a department.");
      return;
    }

    setFormError("");
    setCollegeError("");
    setDepartmentError("");
    await registerStudent();
  };

  // Register API
  const registerStudent = async () => {
    let date = new Date();
    const payload = {
      RoleId: 2,
      FirstName: firstName.trim(),
      LastName: lastName.trim(),
      CollegeId: parseInt(collegeId, 10),
      DepartmentId: parseInt(departmentId, 10),
      Year: date.getFullYear(),
      Email: email.trim().toLowerCase(),
      PhoneNumber: cleanPhoneNumber(phoneNumber) || phoneNumber.trim(),
    };

    setIsSubmitting(true);
    setFormError("");

    try {
      let response = await RegisterStudent(payload);

      if (response && response.success) {
        handleClickOpen();
        setTimeout(() => handleClose(), 6000);
        setTimeout(() => navigate("/"), 3000);
      } else {
        const errorMsg =
          response?.message ||
          "Unable to complete registration. Please check your details and try again.";
        setFormError(errorMsg);
      }
    } catch (err) {
      setFormError("Network error. Please check your connection and try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const img = new Image();
    img.src = require("../../assets/images/Evalutech-Login.webp");
    img.onload = () => setIsLoading(false);
    img.onerror = () => setIsLoading(false);
  }, []);

  // Common select styles to match TextField rounded style
  const selectStyles = {
    borderRadius: "30px",
    backgroundColor: "#fff",
    fontSize: "clamp(13px, 1.1vw, 15px)",
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '30px',
    },
    '& .MuiSelect-select': {
      paddingLeft: '8px',
    },
  };

  // Menu props to limit dropdown height to show ~3 items with scroll
  const menuProps = {
    PaperProps: {
      style: {
        maxHeight: 150, // Approximately 3 items (each ~48px)
        overflowY: 'auto',
      },
    },
  };

  return (
    <>
      {isLoading && <LoadingPopup message="Loading..." />}
      <div
        style={{
          display: "flex",
          minHeight: "100vh",
          width: "100vw",
          backgroundImage: `url(${require("../../assets/images/Evalutech-Login.webp")})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          alignItems: "center",
          justifyContent: "flex-end",
          fontFamily: "'Poppins', sans-serif",
          position: "relative",
        }}
      >
        {/* Top Left Sunware Technologies Logo Box Wrapper for Shadow */}
        <div style={{ position: 'absolute', top: '1.1vw', left: 0, filter: 'drop-shadow(0px 4px 15px rgba(0,0,0,0.08))' }}>
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '0.9vw 4.6vw 0.9vw 1.9vw',
              clipPath: 'polygon(0 0, 100% 0, 82% 100%, 0% 100%)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.9vw',
            }}
          >
            <img
              src={require("../../assets/images/sunwareLogo.png")}
              alt="Sunware Logo"
              style={{ height: '2.73vw' }}
            />
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <span style={{ fontWeight: '800', color: '#1E293B', fontSize: '1.4vw', lineHeight: '1.1', letterSpacing: '0.04vw' }}>
                SUNWARE
              </span>
              <span style={{ fontWeight: '600', color: '#64748B', fontSize: '0.78vw', letterSpacing: '0.08vw' }}>
                TECHNOLOGIES
              </span>
            </div>
          </div>
        </div>

        <div
          style={{
            width: "90%",
            maxWidth: "clamp(350px, 30vw, 500px)",
            maxHeight: "95vh",
            overflowY: "auto",
            boxSizing: "border-box",
            backgroundColor: "#ffffff",
            borderRadius: "24px",
            padding: "clamp(20px, 2.5vw, 30px) clamp(25px, 3vw, 40px)",
            marginRight: "6%",
            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.1)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Logo */}
          <div style={{ marginBottom: "clamp(8px, 1vw, 15px)" }}>
            <img src={require("../../assets/images/Evalutech.png")} alt="Evalutech Logo" style={{ height: "clamp(35px, 3.5vw, 55px)" }} />
          </div>

          <Typography style={{ color: "#F59E0B", fontSize: "clamp(16px, 1.8vw, 22px)", fontWeight: "700", marginBottom: "clamp(12px, 1.5vw, 20px)" }}>
            Ready to take the first step?
          </Typography>

          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 'clamp(8px, 1vw, 12px)' }}>
              <img src={require("../../assets/images/Animation - 1699989202315.gif")} alt="Register Icon" style={{ width: 'clamp(24px, 2.5vw, 35px)' }} />
              <Typography style={{ color: '#4F46E5', fontWeight: '700', fontSize: 'clamp(12px, 1vw, 15px)', letterSpacing: '1px' }}>
                REGISTER
              </Typography>
            </div>

            <form onSubmit={handleSubmit} style={{ width: '100%' }}>
              {/* First Name & Last Name */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="First Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  error={!!firstNameError}
                  InputProps={{
                    startAdornment: <PersonOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
                <TextField
                  placeholder="Last Name"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  error={!!lastNameError}
                  InputProps={{
                    startAdornment: <PersonOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              {/* Email */}
              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="Email"
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  error={!!emailError}
                  InputProps={{
                    startAdornment: <MailOutlineIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              {/* Phone Number */}
              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <TextField
                  placeholder="Phone Number"
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ maxLength: 14 }}
                  value={phoneNumber}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw.length > 10 || /\D/.test(raw)) {
                      setPhoneNumber(cleanPhoneNumber(raw));
                    } else {
                      setPhoneNumber(raw);
                    }
                  }}
                  error={!!phoneError}
                  InputProps={{
                    startAdornment: <PhoneOutlinedIcon style={{ color: '#9CA3AF', marginRight: '6px', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />,
                    style: { borderRadius: "30px", backgroundColor: "#fff", fontSize: "clamp(13px, 1.1vw, 15px)" }
                  }}
                />
              </div>

              {/* College Dropdown */}
              <div style={{ marginBottom: 'clamp(8px, 1vw, 12px)' }}>
                <FormControl fullWidth size="small" error={!!collegeError}>
                  <Select
                    value={collegeId}
                    onChange={(e) => setCollegeId(e.target.value)}
                    displayEmpty
                    disabled={loadingColleges}
                    sx={selectStyles}
                    MenuProps={menuProps}
                    startAdornment={
                      <InputAdornment position="start">
                        <AccountBalanceOutlinedIcon style={{ color: '#9CA3AF', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />
                      </InputAdornment>
                    }
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#9CA3AF' }}>Select College</span>;
                      }
                      const college = colleges.find(c => c.Id === selected);
                      return college ? college.Name : '';
                    }}
                  >
                    {loadingColleges ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} style={{ marginRight: 10 }} />
                        Loading colleges...
                      </MenuItem>
                    ) : colleges.length === 0 ? (
                      <MenuItem disabled>No colleges available</MenuItem>
                    ) : (
                      colleges.map((college) => (
                        <MenuItem key={college.Id} value={college.Id}>
                          {college.Name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </div>

              {/* Department Dropdown (dependent on College) */}
              <div style={{ marginBottom: 'clamp(12px, 1.5vw, 18px)' }}>
                <FormControl fullWidth size="small" error={!!departmentError}>
                  <Select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    displayEmpty
                    disabled={!collegeId || loadingDepartments}
                    sx={selectStyles}
                    MenuProps={menuProps}
                    startAdornment={
                      <InputAdornment position="start">
                        <AutoStoriesOutlinedIcon style={{ color: '#9CA3AF', fontSize: 'clamp(16px, 1.5vw, 20px)' }} />
                      </InputAdornment>
                    }
                    renderValue={(selected) => {
                      if (!collegeId) {
                        return <span style={{ color: '#9CA3AF' }}>Select college first</span>;
                      }
                      if (!selected) {
                        return <span style={{ color: '#9CA3AF' }}>Select Department</span>;
                      }
                      const dept = departments.find(d => d.Id === selected);
                      return dept ? dept.Name : '';
                    }}
                  >
                    {loadingDepartments ? (
                      <MenuItem disabled>
                        <CircularProgress size={20} style={{ marginRight: 10 }} />
                        Loading departments...
                      </MenuItem>
                    ) : departments.length === 0 ? (
                      <MenuItem disabled>
                        {collegeId ? "No departments available" : "Select a college first"}
                      </MenuItem>
                    ) : (
                      departments.map((dept) => (
                        <MenuItem key={dept.Id} value={dept.Id}>
                          {dept.Name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </div>

              {/* Error Messages */}
              {formError && (
                <Typography style={{ color: 'red', fontSize: 'clamp(11px, 0.9vw, 13px)', marginBottom: '10px', textAlign: 'center' }}>
                  {formError}
                </Typography>
              )}
              {(firstNameError || lastNameError || emailError || phoneError) && (
                <Typography style={{ color: 'red', fontSize: 'clamp(11px, 0.9vw, 13px)', marginBottom: '10px', textAlign: 'center' }}>
                  Please check the input fields.
                </Typography>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                disabled={isSubmitting || loadingColleges}
                style={{
                  backgroundColor: isSubmitting ? "#D97706" : "#F59E0B",
                  color: "#fff",
                  borderRadius: "30px",
                  padding: "clamp(8px, 1vw, 12px)",
                  fontSize: "clamp(14px, 1.2vw, 17px)",
                  fontWeight: "600",
                  textTransform: "none",
                  display: "flex",
                  gap: "8px",
                  boxShadow: '0 4px 10px rgba(245, 158, 11, 0.3)',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  opacity: isSubmitting ? 0.8 : 1
                }}
              >
                {isSubmitting ? "Creating Account..." : "Create Account"} <ArrowForwardIcon fontSize="clamp(15px, 1.3vw, 18px)" />
              </Button>
            </form>

            <Typography style={{ textAlign: "center", marginTop: "clamp(15px, 2vw, 25px)", fontSize: "clamp(12px, 1.1vw, 14px)", color: "#6B7280" }}>
              Already have an account?{" "}
              <Link to={"/login"} style={{ color: "#4F46E5", textDecoration: "none", fontWeight: "600" }}>
                Login
              </Link>
            </Typography>
          </div>
        </div>

        <Dialog 
          open={open} 
          onClose={handleClose}
          PaperProps={{
            style: {
              borderRadius: '20px',
              padding: '20px',
              maxWidth: '400px',
              textAlign: 'center',
              overflow: 'visible'
            }
          }}
        >
          <DialogContent style={{ overflow: 'visible', padding: '20px 30px 30px' }}>
            {/* Success Icon with Animation */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '-60px auto 20px',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.4)',
              animation: 'popIn 0.5s ease-out'
            }}>
              <svg 
                width="40" 
                height="40" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="white" 
                strokeWidth="3" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                style={{ animation: 'checkmark 0.5s ease-out 0.3s both' }}
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>

            {/* Title */}
            <Typography style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#1F2937',
              marginBottom: '10px'
            }}>
              Registration Successful!
            </Typography>

            {/* Subtitle */}
            <Typography style={{
              fontSize: '15px',
              color: '#6B7280',
              marginBottom: '20px',
              lineHeight: '1.6'
            }}>
              Your account has been created successfully. 
              <br />
              <span style={{ color: '#10B981', fontWeight: '600' }}>Redirecting to login...</span>
            </Typography>

            {/* Progress Bar */}
            <div style={{
              width: '100%',
              height: '4px',
              backgroundColor: '#E5E7EB',
              borderRadius: '2px',
              overflow: 'hidden',
              marginBottom: '20px'
            }}>
              <div style={{
                width: '100%',
                height: '100%',
                background: 'linear-gradient(90deg, #10B981, #059669)',
                borderRadius: '2px',
                animation: 'progressBar 3s linear forwards'
              }}></div>
            </div>

            {/* Decorative Elements */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px'
            }}>
              {[...Array(3)].map((_, i) => (
                <div key={i} style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: i === 0 ? '#10B981' : i === 1 ? '#F59E0B' : '#4F46E5',
                  animation: `bounce 1s ease-in-out ${i * 0.2}s infinite`
                }}></div>
              ))}
            </div>

            {/* CSS Animations */}
            <style>{`
              @keyframes popIn {
                0% { transform: scale(0); opacity: 0; }
                50% { transform: scale(1.2); }
                100% { transform: scale(1); opacity: 1; }
              }
              @keyframes checkmark {
                0% { stroke-dasharray: 100; stroke-dashoffset: 100; }
                100% { stroke-dashoffset: 0; }
              }
              @keyframes progressBar {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(0%); }
              }
              @keyframes bounce {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-5px); }
              }
            `}</style>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default Register;
